import React, { useEffect, useLayoutEffect, lazy, Suspense, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import logo from '../../sidebar-logo.png';
import mixpanel from 'mixpanel-browser';
import {
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  PlayArrow as PlayArrowIcon,
  Extension as ExtensionIcon,
  PhoneIphone as PhoneIphoneIcon,
  Computer as ComputerIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { setLandingPage } from '../../utils/trackingParams';
import { logConversion } from '../../firebase';
import userStore from '../../Stores/User';
import { isPaidProUser, hasPaidPlan } from '../../constants/plans';
// Import SCSS synchronously to ensure styles are loaded before render
import './landingA.scss';

const Testimonials = lazy(() => import('./Components/Testimonials'));
const requestIdleCallback = window.requestIdleCallback || ((callback) => setTimeout(callback, 1));

const ProBadge = observer(() => {
  const isProUser = userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id 
    ? isPaidProUser(userStore.user)
    : false;
  const hasPlan = hasPaidPlan(userStore.user);
  const [showPopover, setShowPopover] = React.useState(false);
  const badgeStyle = {
    background: isProUser 
      ? 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)' 
      : hasPlan 
        ? 'linear-gradient(135deg, #FFB347 0%, #FFCC33 100%)'
        : 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: 'bold',
    position: 'absolute',
    top: isProUser ? '35px' : '0px',
    left: '45px',
    display: 'inline-flex',
    alignItems: 'center',
    height: '16px',
    width: isProUser ? '32px' : hasPlan ? '70px' : '95px',
    justifyContent: 'center',
    cursor: isProUser ? 'default' : 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    color: 'white'
  };
  const popoverStyle = {
    position: 'absolute',
    top: '25px',
    left: '45px',
    background: 'rgba(0, 0, 0, 0.9)',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    width: '200px',
    color: 'white',
    zIndex: 1000,
    pointerEvents: 'none',
    opacity: showPopover ? 1 : 0,
    transition: 'opacity 0.2s ease-in-out',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(5px)'
  };
  if (!userStore.user) return null;
  if (isProUser) return <div style={badgeStyle}>PRO</div>;
  if (hasPlan) {
    return (
      <div style={{ position: 'relative' }}>
        <a href={userStore.customerPortalUrl} target="_blank" rel="noreferrer" style={badgeStyle}
          onMouseEnter={() => setShowPopover(true)} onMouseLeave={() => setShowPopover(false)}>
          UPGRADE
        </a>
        <div style={popoverStyle}>Upgrade to pro to get premium voices, other languages, and more!</div>
      </div>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <Link to="/onboarding/ChooseVoice" style={badgeStyle}
        onMouseEnter={() => setShowPopover(true)} onMouseLeave={() => setShowPopover(false)}>
        START TRIAL
      </Link>
      <div style={popoverStyle}>Start your 7-day free trial today!</div>
    </div>
  );
});

const LandingHowItWorks = observer(() => {
  const cta = "Start listening";
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  useLayoutEffect(() => {
    // Set global styles immediately to ensure proper rendering on direct navigation
    // This matches what App.js does but ensures it happens for this page
    document.body.style.backgroundColor = '#000000';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.backgroundColor = '#000000';
    
    // Scroll to top immediately before paint
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    requestIdleCallback(() => {
      mixpanel.track('How It Works Landing Page Visited', { page: 'landing_how_it_works' });
      setLandingPage('landing_how_it_works');
    });
  }, []);

  const renderSignUpButton = useCallback(() => {
    return (
      <div className="cta-div" style={{ position: 'static', width: 'auto' }}>
        <Link to="/signin?source=nav_signup&redirected_from=how-it-works" className="cta-button"
          onClick={() => setTimeout(() => { mixpanel.track('Click Sign Up (Nav)', { placement: 'HowItWorks' }); logConversion('sign_up_click', 'A'); }, 0)}>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}><div><div>{cta}</div></div></div>
        </Link>
      </div>
    );
  }, [cta]);

  const renderCta = useCallback(() => {
    return (
      <div className="cta-div" style={{ position: 'static' }}>
        <Link to="/signin?source=how-it-works&redirected_from=how-it-works" className="cta-button"
          onClick={() => setTimeout(() => { mixpanel.track('Click Try For Free', { placement: 'HowItWorks' }); logConversion('try_for_free_click', 'A'); }, 0)}>
          <div style={{ display: 'flex', justifyContent: 'space-around' }}><div><div>Start Listening</div></div></div>
        </Link>
      </div>
    );
  }, []);

  return (
    <div className="App" style={{ contain: 'layout style paint' }}>
      <Helmet>
        <title>From Words to Sound in Seconds | How AudioRead Works</title>
        <meta name="description" content="Learn how our AI converts any text into crystal-clear audio. Simple steps, powerful results. See how fast you can start listening." />
        <meta property="og:title" content="From Words to Sound in Seconds | How AudioRead Works" />
        <meta property="og:description" content="Learn how our AI converts any text into crystal-clear audio. Simple steps, powerful results. See how fast you can start listening." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="https://audioread.com/logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="From Words to Sound in Seconds | How AudioRead Works" />
        <meta name="twitter:description" content="Learn how our AI converts any text into crystal-clear audio. Simple steps, powerful results. See how fast you can start listening." />
        <meta name="twitter:image" content="https://audioread.com/logo.png" />
      </Helmet>
      <link rel="preload" href={logo} as="image" />
      
      <header className="App-header" style={{ minHeight: '100vh' }}>
        <div className="nav-bar">
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <div style={{ position: "relative" }} className="logo-container">
              <Link to="/"><img className="audiblogs-logo" src={logo} alt="logo" loading="eager" decoding="sync" style={{ contain: 'layout style paint' }} /></Link>
              <ProBadge />
            </div>
          </div>
          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '20px', fontSize: '1.05rem', fontWeight: 'bold' }}>
              <Link className="signin-button" to="/signin?source=nav_signin&redirected_from=how-it-works">Sign In</Link>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', marginLeft: '-20px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
          <div className="mobile-nav">
            <div style={{display: 'flex', alignItems: 'center', gap: '10px', transform: 'scale(0.75)', paddingBottom: '15px', marginRight: '-10px'}}>
              {renderSignUpButton()}
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '8px',
                  marginLeft: '5px',
                  width: '44px',
                  height: '44px',
                  minWidth: '44px',
                  minHeight: '44px',
                  flexShrink: 0
                }}
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <CloseIcon style={{ fontSize: '28px' }} /> : <MenuIcon style={{ fontSize: '28px' }} />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation Menu (shared for desktop and mobile) - Outside nav-bar to prevent clipping */}
        {isMenuOpen && (
          <div 
            style={{
              position: 'fixed',
              top: '80px',
              right: '20px',
              backgroundColor: 'rgba(9, 2, 19, 0.95)',
              padding: '20px',
              width: '220px',
              borderRadius: '10px',
              backdropFilter: 'blur(10px)',
              zIndex: 10000,
              boxSizing: 'border-box',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'visible'
            }}
          >
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link
                to="/features"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Features from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Features
              </Link>
              <Link
                to="/how-it-works"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click How It Works from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                How It Works
              </Link>
              <Link
                to="/integrations-and-platforms"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Integrations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Integrations
              </Link>
              <Link
                to="/solutions-and-accessibility"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Solutions from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Solutions
              </Link>
              <Link
                to="/organizations"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click Organizations from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                Organizations
              </Link>
              <Link
                to="/api"
                className="mobile-menu-link"
                onClick={() => {
                  mixpanel.track('Click API from Menu');
                  setIsMenuOpen(false);
                }}
                style={{ color: '#fff', textDecoration: 'none', fontSize: '16px', padding: '8px 0' }}
              >
                API
              </Link>
            </div>
          </div>
        )}
        <h1 className="bigger-headline" style={{ marginTop: '60px', paddingTop: '0px' }}>How Text-to-Speech Works with Audioread</h1>
        <h2 className="subheadline">Curious about how text-to-speech works in practice? Audioread makes it effortless. In just a few steps, you can convert articles, emails, or documents into natural audio and stream them like a podcast, no technical skills required.</h2>
        <p className="details" style={{ marginTop: '20px' }}>Whether you're based in the UK or listening globally, our tool is designed for ease, clarity, and productivity.</p>
      </header>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <CloudUploadIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Step 1: Input Any Text</h1>
        </div>
        <p className="details" style={{ marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px' }}>Start by adding your content. You can:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Paste a link to an article or webpage
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Upload a PDF or forward an email
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Highlight text in your browser
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Send content via RSS feed
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>This makes Audioread a flexible and powerful audio conversion tool. How to use it depends entirely on your workflow.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <AutoAwesomeIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Step 2: Let the AI Do the Work</h1>
        </div>
        <p className="details">Once you've added your content, Audioread's engine gets to work. Here's what happens behind the scenes in the AI audio conversion process:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Text is processed and segmented for clarity
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            AI voices are selected based on natural flow and length
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Your content is narrated using lifelike speech patterns
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            The final result is delivered in clear, human-grade audio
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>Our UK-based platform prioritises long-form listening comfort. Whether it's a newsletter, research paper, or technical doc, the result feels more audiobook than robot.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <PlayArrowIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Step 3: Stream or Download</h1>
        </div>
        <p className="details">After conversion, you can:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Stream your audio via a private podcast feed (Apple, Spotify, Overcast, and more)
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Play it in your browser (no app installation needed)
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Download as MP3 for offline listening or storage
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>This process is ideal for those looking to build a habit of listening instead of reading.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <ExtensionIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Use It Your Way</h1>
        </div>
        <p className="details">Audioread fits seamlessly into daily routines:</p>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #4292fa 0%, #4caf50 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ExtensionIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div>Convert directly from your browser with the extension</div>
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #CB5EEE 0%, #4BE1EC 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PhoneIphoneIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div>Use the Safari Shortcut on iPhone or iPad</div>
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ComputerIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div>Access via Android Progressive Web App</div>
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '60px', position: 'relative', display: 'flex', alignItems: 'flex-start' }}>
            <div style={{ position: 'absolute', left: 0, top: '2px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', borderRadius: '50%', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmailIcon style={{ fontSize: '24px', color: '#fff' }} />
            </div>
            <div>Drag-and-drop documents or copy and paste content</div>
          </li>
        </ul>
        <p className="details" style={{ marginTop: '20px', maxWidth: '800px', margin: '20px auto 0' }}>It's a simple, repeatable process that saves time and makes listening effortless.</p>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
          <PeopleIcon style={{ fontSize: '40px', background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
          <h1 className="headline" style={{ margin: 0, background: 'linear-gradient(135deg, #8535cd 0%, #e82186 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Who Uses Audioread?</h1>
        </div>
        <ul style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto', color: '#fff', fontSize: '18px', lineHeight: '1.8', listStyle: 'none', padding: 0 }}>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Commuters converting reading lists into playlists
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Students revising notes on the move
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            Professionals consuming industry reports in audio form
          </li>
          <li style={{ marginBottom: '15px', paddingLeft: '30px', position: 'relative' }}>
            <span style={{ position: 'absolute', left: 0 }}>•</span>
            UK-based users enhancing productivity during everyday tasks
          </li>
        </ul>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline" style={{ marginBottom: '30px' }}>What Our Users Say</h1>
        <Suspense fallback={<div style={{ textAlign: 'center', padding: '40px' }}>Loading testimonials...</div>}>
          <Testimonials />
        </Suspense>
      </div>

      <div className="App-header" style={{ display: 'flex', flexDirection: 'column', gap: '20px', margin: '0 auto', padding: '60px 0' }}>
        <h1 className="headline">Ready to Listen Smarter?</h1>
        <p className="details">
          Explore how Audioread's <strong>text-to-speech features</strong> help you convert, stream, and learn on your terms.
        </p>
        <div style={{ marginTop: '30px' }}>
          {renderCta()}
        </div>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Link to="/features" style={{ 
            color: '#4292fa', 
            textDecoration: 'none', 
            fontSize: '18px', 
            fontWeight: 'bold',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, rgba(66, 146, 250, 0.2) 0%, rgba(76, 175, 80, 0.2) 100%)',
            borderRadius: '10px',
            border: '1px solid rgba(66, 146, 250, 0.3)',
            display: 'inline-block',
            transition: 'all 0.3s ease'
          }}>Explore Features →</Link>
        </div>
      </div>

      <div className="footer-links" style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/dmca">DMCA Policy</Link>
        <a href="https://audioread.tolt.io/" target="_blank" rel="noopener noreferrer">Affiliate Program</a>
      </div>
      <div style={{ margin: '25px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
          
      </div>
      <div style={{ margin: '25px', opacity: 0.7 }}>© {new Date().getFullYear()} Audioread™. All rights reserved. | <a href="mailto:info@audioread.com" target="_blank" rel="noopener noreferrer">
          Contact us at info@audioread.com
        </a></div>
    </div>
  );
});

export default LandingHowItWorks;

